import IContact from '@constants/interfaces/contacts';
import { WEB_AUTH_STORAGE_KEY } from '../constants/authStorage';

const baseURL = process.env.REACT_APP_BASE_URL || 'http://localhost:8000/';

// 401 or 403 ACCOUNT_BLOCKED: clear session and redirect to login
function handleAuthFailure(res: Response, data: Record<string, unknown>): void {
  const isBlocked = res.status === 403 && data?.code === 'ACCOUNT_BLOCKED';
  const isUnauth = res.status === 401;
  if (isBlocked || isUnauth) {
    try {
      localStorage.removeItem(WEB_AUTH_STORAGE_KEY);
    } catch {
      // ignore
    }
    if (typeof window !== 'undefined') {
      window.location.href = `${window.location.origin}/login`;
    }
  }
}

/**
 * Session token for contact calls. `/v2` requires one on every contact route (the server then
 * enforces ownership); `/v1` is deprecated and token-less. Sent in the query string for requests
 * without a body and in the JSON body otherwise, so it stays out of URLs (and access logs) where
 * there is somewhere better to put it.
 */
function getAuthToken(): string {
  try {
    const raw = localStorage.getItem(WEB_AUTH_STORAGE_KEY);
    if (!raw || raw === 'undefined' || raw === 'null') return '';
    return (JSON.parse(raw) as { AuthToken?: string })?.AuthToken || '';
  } catch {
    return '';
  }
}

function withAuth(url: string): string {
  const token = getAuthToken();
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}authToken=${encodeURIComponent(token)}`;
}

function authBody(payload: Record<string, unknown>): string {
  const token = getAuthToken();
  return JSON.stringify(token ? { ...payload, authToken: token } : payload);
}

export const googleAuthApi = async (idToken: string) => {
  try {
    const res = await fetch(baseURL + 'v2/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
    });
    return res.json();
  } catch (error) {
    console.error(error);
    return { ok: false, error: 'Request failed' };
  }
};

export type LoginResponse =
  | {
      ok: true;
      data: {
        UserId: string;
        AuthToken: string;
        UserEmail?: string | null;
        UserPhone?: string | null;
      };
    }
  | { ok: false; result?: string };

export const loginApi = async (values: any): Promise<LoginResponse> => {
  try {
    const res = await fetch(baseURL + 'v2/login', {
      method: 'POST',
      body: JSON.stringify(values),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      data?: LoginResponse extends { ok: true; data: infer D } ? D : never;
      result?: string;
    };
    if (!res.ok) {
      const msg =
        data?.result ??
        (data as { error?: { message?: string } })?.error?.message ??
        'Invalid email/phone or password.';
      return {
        ok: false,
        result: String(msg).trim() || 'Invalid email/phone or password.',
      };
    }
    if (data?.ok === true && data?.data) return { ok: true, data: data.data };
    return {
      ok: false,
      result: data?.result ?? 'Invalid email/phone or password.',
    };
  } catch (error) {
    console.error(error);
    return { ok: false, result: 'Request failed – check your connection.' };
  }
};

export const registerApi = async (
  values: any
): Promise<{ ok: boolean; result?: unknown; error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/user', {
      method: 'POST',
      body: JSON.stringify(values),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    const getMsg = (fallback: string) => {
      const msg =
        data?.result ??
        (data?.error as { message?: string })?.message ??
        (typeof data?.error === 'string' ? data.error : null);
      return (msg && String(msg).trim()) || fallback;
    };
    if (!res.ok) {
      const fallback =
        res.status === 0
          ? 'Network error – check your connection and that the API is running.'
          : 'Registration failed. Check that the API is running (e.g. ' +
            baseURL +
            'v2/user).';
      return { ok: false, error: getMsg(fallback) };
    }
    if (data?.ok === true) return data as { ok: boolean; result?: unknown };
    return { ok: false, error: getMsg('Registration failed') };
  } catch (error) {
    console.error(error);
    return { ok: false, error: 'Request failed – check your connection.' };
  }
};

export const forgotPasswordApi = async (payload: {
  email?: string;
  phone?: string;
}): Promise<{ ok?: boolean; error?: string; message?: string; channel?: 'email' | 'admin' }> => {
  try {
    const hasEmail =
      payload.email != null && String(payload.email).trim() !== '';
    const hasPhone =
      payload.phone != null && String(payload.phone).trim() !== '';
    if (!hasEmail && !hasPhone) return { error: 'Email or phone is required' };
    const body = hasEmail
      ? { email: String(payload.email).trim() }
      : { phone: String(payload.phone).trim() };
    const res = await fetch(baseURL + 'v2/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string;
      message?: string;
      channel?: 'email' | 'admin';
    };
    if (!res.ok) return { error: data?.error || 'Request failed' };
    return { ok: data?.ok, message: data?.message, channel: data?.channel };
  } catch (error) {
    console.error(error);
    return { error: 'Request failed' };
  }
};

export const changePasswordApi = async (
  currentPassword: string,
  newPassword: string,
  authToken: string
): Promise<{ ok: boolean; data?: Record<string, unknown> } | undefined> => {
  try {
    const res = await fetch(baseURL + 'v2/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        currentPassword,
        newPassword,
        authToken,
      }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    return { ok: res.ok, data };
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const resetPasswordApi = async (token: string, newPassword: string) => {
  try {
    const res = await fetch(baseURL + 'v2/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    return res;
  } catch (error) {
    console.error(error);
  }
};

export const logoutApi = async (
  authToken: string
): Promise<{ ok?: boolean }> => {
  try {
    const res = await fetch(baseURL + 'v2/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = await res.json().catch(() => ({}));
    return data as { ok?: boolean };
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getUserEmail = async (loggedInUserId: string) => {
  try {
    const res = await fetch(withAuth(baseURL + `v2/contact/user/${loggedInUserId}`), {
      method: 'GET',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      handleAuthFailure(res, data as Record<string, unknown>);
      return undefined;
    }
    return data.user;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Create or update a Contact. Resolves to the saved Contact, or null on failure.
 *
 * It used to return `res.ok`, throwing the saved record away — which is why a newly created Contact
 * had no id to navigate to and every save dumped the user back at the top of the list.
 */
export const addNewContact = async (values: any): Promise<IContact | null> => {
  try {
    const res = await fetch(baseURL + 'v2/contact', {
      method: 'POST',
      body: authBody(values),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) return null;
    const saved = await res.json().catch(() => null);
    return saved && typeof saved === 'object' ? (saved as IContact) : null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const deleteContactApi = async (contactSelected: IContact) => {
  try {
    const res = await fetch(baseURL + 'v2/contact', {
      method: 'DELETE',
      body: authBody(contactSelected as unknown as Record<string, unknown>),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const isSuccess = await res.ok;
    return isSuccess;
  } catch (error) {
    console.error(error);
  }
};

export const getContactList = async (loggedInUserId: string) => {
  try {
    const res = await fetch(withAuth(baseURL + `v2/contact/all/${loggedInUserId}`), {
      method: 'GET',
    });
    if (!res.ok) {
      handleAuthFailure(res, (await res.json().catch(() => ({}))) as Record<string, unknown>);
      return [];
    }
    return res.json();
  } catch (error) {
    console.error(error);
  }
};

/** The user's Tag vocabulary (v2-only). Replaces deriving tags from the whole contact list. */
export const getTagsApi = async (loggedInUserId: string | number): Promise<string[] | null> => {
  try {
    const res = await fetch(withAuth(baseURL + `v2/contact/tags/${loggedInUserId}`), { method: 'GET' });
    if (!res.ok) return null;
    const body = await res.json();
    if (!Array.isArray(body?.tags)) return null;
    return body.tags.filter((t: unknown): t is string => typeof t === 'string' && !!t.trim());
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * The Contact currently holding a LinkedIn Profile URL, if any (v2-only).
 * ADR-0002: a Profile URL is not an identity, so this is advisory — used to warn about a likely
 * duplicate, never to block a save or to merge records.
 */
export const getContactByUrlApi = async (profileUrl: string): Promise<IContact | null> => {
  try {
    const res = await fetch(
      withAuth(baseURL + 'v2/contact/by-url?url=' + encodeURIComponent(profileUrl)),
      { method: 'GET' }
    );
    if (!res.ok) return null;
    const body = await res.json();
    return body?.contact ?? null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getContactById = async (contactId: number | string): Promise<IContact | null> => {
  try {
    const res = await fetch(withAuth(baseURL + `v2/contact/${contactId}`), { method: 'GET' });
    if (!res.ok) {
      handleAuthFailure(res, (await res.json().catch(() => ({}))) as Record<string, unknown>);
      return null;
    }
    return (await res.json()) as IContact;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const addNoteMessageApi = async (
  contactId: number,
  body: string
): Promise<{ message?: unknown; error?: string }> => {
  try {
    const res = await fetch(baseURL + `v2/contact/${contactId}/note`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: authBody({ Body: body }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      return {
        error: (data as { error?: string })?.error || 'Failed to add note',
      };
    return { message: data };
  } catch (error) {
    console.error(error);
    return { error: 'Failed to add note' };
  }
};

export const addReminderMessageApi = async (
  contactId: number,
  body: string
): Promise<{ message?: unknown; error?: string }> => {
  try {
    const res = await fetch(baseURL + `v2/contact/${contactId}/reminder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: authBody({ Body: body }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      return {
        error: (data as { error?: string })?.error || 'Failed to add reminder',
      };
    return { message: data };
  } catch (error) {
    console.error(error);
    return { error: 'Failed to add reminder' };
  }
};

export const deleteReminderMessageApi = async (
  contactId: number,
  reminderId: number
): Promise<{ ok: boolean; error?: string }> => {
  try {
    const res = await fetch(withAuth(baseURL + `v2/contact/${contactId}/reminder/${reminderId}`), { method: 'DELETE' });
    if (res.ok) return { ok: true };
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    return { ok: false, error: data?.error || 'Failed to remove reminder' };
  } catch (error) {
    console.error(error);
    return { ok: false, error: 'Failed to remove reminder' };
  }
};

export const getSubscriptionStatusApi = async (authToken: string) => {
  try {
    const res = await fetch(
      baseURL +
        'v2/stripe/subscription-status?authToken=' +
        encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    return data;
  } catch (error) {
    console.error(error);
    return { hasAccess: false, status: null };
  }
};

export type CreateCheckoutSessionResult = {
  url?: string;
  error?: string;
  redirectToPortal?: boolean;
  hasAccess?: boolean;
};

export const createCheckoutSessionApi = async (
  authToken: string,
  successUrl?: string,
  cancelUrl?: string
): Promise<CreateCheckoutSessionResult> => {
  try {
    const res = await fetch(baseURL + 'v2/stripe/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, successUrl, cancelUrl }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok) {
      return {
        ...(data as object),
        error:
          (data as { error?: string })?.error ||
          res.statusText ||
          'Checkout failed',
      } as CreateCheckoutSessionResult;
    }
    return data as CreateCheckoutSessionResult;
  } catch (error) {
    console.error(error);
    return { error: 'Request failed. Check your connection and try again.' };
  }
};

export const verifyCheckoutSessionApi = async (
  authToken: string,
  sessionId: string
) => {
  try {
    const res = await fetch(baseURL + 'v2/stripe/verify-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, session_id: sessionId }),
    });
    return res.json();
  } catch (error) {
    console.error(error);
    return { hasAccess: false };
  }
};

export const verifyCheckoutSessionBySessionIdApi = async (
  sessionId: string
) => {
  try {
    const res = await fetch(baseURL + 'v2/stripe/verify-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    return res.json();
  } catch (error) {
    console.error(error);
    return { hasAccess: false };
  }
};

export const createCustomerPortalApi = async (
  authToken: string,
  returnUrl?: string
): Promise<{ url?: string; error?: string } | undefined> => {
  try {
    const res = await fetch(baseURL + 'v2/stripe/customer-portal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, returnUrl }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    return data as { url?: string; error?: string };
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const getAdminListApi = async (
  authToken: string
): Promise<{
  adminUserIds?: number[];
  superAdminUserId?: number | null;
  error?: string;
}> => {
  try {
    const res = await fetch(
      baseURL + 'v2/admin/list?authToken=' + encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {
      adminUserIds: (data as { adminUserIds?: number[] }).adminUserIds ?? [],
      superAdminUserId:
        (data as { superAdminUserId?: number | null }).superAdminUserId ?? null,
    };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const addAdminByEmailOrPhoneApi = async (
  authToken: string,
  payload: { email?: string; phone?: string }
): Promise<{ error?: string; userId?: number }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, ...payload }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { error?: string; userId?: number };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: data?.error || 'Failed' };
    return { userId: data.userId };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const putAdminListApi = async (
  authToken: string,
  adminUserIds: number[]
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/list', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, adminUserIds }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export type AdminUserRow = {
  id: number;
  email: string | null;
  phone: string | null;
  displayName: string | null;
  createdAt: string | null;
  subscriptionStatus: string | null;
  blockedAt: string | null;
  contactCount: number;
  isOnline?: boolean;
  lastSeenAt?: string | null;
};

export const getAdminCountsApi = async (
  authToken: string
): Promise<{
  users?: number;
  admins?: number;
  notifications?: number;
  error?: string;
}> => {
  try {
    const res = await fetch(
      baseURL + 'v2/admin/counts?authToken=' + encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & {
      users?: number;
      admins?: number;
      notifications?: number;
      error?: string;
    };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: data?.error || 'Failed' };
    return {
      users: data.users ?? 0,
      admins: data.admins ?? 0,
      notifications: data.notifications ?? 0,
    };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const getAdminUsersApi = async (
  authToken: string
): Promise<{ users?: AdminUserRow[]; error?: string }> => {
  try {
    const res = await fetch(
      baseURL + 'v2/admin/users?authToken=' + encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { users?: AdminUserRow[]; error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: data?.error || 'Failed' };
    return { users: data.users ?? [] };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const adminResetPasswordApi = async (
  authToken: string,
  payload: { userId?: number; email?: string; newPassword: string }
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, ...payload }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const deactivateAccountApi = async (
  authToken: string,
  userId: number
): Promise<{ error?: string; ok?: boolean; scheduledDeletionAt?: string }> => {
  try {
    const url =
      baseURL +
      'v2/admin/user/' +
      userId +
      '?authToken=' +
      encodeURIComponent(authToken);
    const res = await fetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return data as { ok?: boolean; scheduledDeletionAt?: string };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const deleteUserApi = deactivateAccountApi;

export const getAccountMeApi = async (
  authToken: string
): Promise<{
  email: string | null;
  phone: string | null;
  scheduledDeletionAt: string | null;
  error?: string;
}> => {
  try {
    const res = await fetch(
      baseURL + 'v2/account/me?authToken=' + encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return {
        email: null,
        phone: null,
        scheduledDeletionAt: null,
        error: (data as { error?: string })?.error,
      };
    const d = data as {
      email?: string | null;
      phone?: string | null;
      scheduledDeletionAt?: string | null;
    };
    return {
      email: d.email ?? null,
      phone: d.phone ?? null,
      scheduledDeletionAt: d.scheduledDeletionAt ?? null,
    };
  } catch (e) {
    console.error(e);
    return {
      email: null,
      phone: null,
      scheduledDeletionAt: null,
      error: 'Request failed',
    };
  }
};

export const patchAccountContactApi = async (
  authToken: string,
  payload: { email?: string; phone?: string }
): Promise<{ email: string | null; phone: string | null; error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/account/contact', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, ...payload }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return {
        email: null,
        phone: null,
        error: (data as { error?: string })?.error || 'Failed',
      };
    const d = data as { email?: string | null; phone?: string | null };
    return { email: d.email ?? null, phone: d.phone ?? null };
  } catch (e) {
    console.error(e);
    return { email: null, phone: null, error: 'Request failed' };
  }
};

export const getAccountDeletionStatusApi = async (
  authToken: string
): Promise<{ scheduledDeletionAt: string | null; error?: string }> => {
  try {
    const res = await fetch(
      baseURL +
        'v2/account/deletion-status?authToken=' +
        encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return {
        scheduledDeletionAt: null,
        error: (data as { error?: string })?.error,
      };
    return {
      scheduledDeletionAt:
        (data as { scheduledDeletionAt?: string | null })
          ?.scheduledDeletionAt ?? null,
    };
  } catch (e) {
    console.error(e);
    return { scheduledDeletionAt: null, error: 'Request failed' };
  }
};

export const reactivateAccountApi = async (
  authToken: string,
  userId: number
): Promise<{ error?: string; ok?: boolean }> => {
  try {
    const res = await fetch(
      baseURL + 'v2/admin/user/' + userId + '/cancel-deletion',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authToken }),
      }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return data as { ok?: boolean };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const cancelAccountDeletionApi = reactivateAccountApi;

export const blockUserApi = async (
  authToken: string,
  userId: number
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/user/' + userId + '/block', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const unblockUserApi = async (
  authToken: string,
  userId: number
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/user/' + userId + '/unblock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export type AdminAnalyticsResponse = {
  totalUsers?: number;
  onlineUsers?: number;
  activeUsersLast30Days?: number;
  totalContacts?: number;
  subscriptionActive?: number;
  subscriptionCanceled?: number;
  newUsersThisWeek?: number;
  newUsersThisMonth?: number;
  from?: string;
  to?: string;
  activeUsersInRange?: number;
  activeUsersPrevious?: number;
  newSignupsInRange?: number;
  newSignupsPrevious?: number;
  error?: string;
};

export const getAdminAnalyticsApi = async (
  authToken: string,
  params?: { from?: string; to?: string }
): Promise<AdminAnalyticsResponse> => {
  try {
    let url =
      baseURL + 'v2/admin/analytics?authToken=' + encodeURIComponent(authToken);
    if (params?.from) url += '&from=' + encodeURIComponent(params.from);
    if (params?.to) url += '&to=' + encodeURIComponent(params.to);
    const res = await fetch(url, { method: 'GET' });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return data as AdminAnalyticsResponse;
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export type AdminConfig = {
  pricingMessage: string;
  supportEmail: string;
  appDisplayName: string;
  termsUrl: string;
  termsContent: string;
  privacyUrl: string;
  privacyContent: string;
  helpUrl: string;
  helpContent: string;
  signupEnabled: string;
  maintenanceMode: string;
  maintenanceMessage: string;
  loginBanner: string;
  registerSuccessMessage: string;
  subscriptionSuccessMessage: string;
  priceLabel: string;
  sessionDurationDays: string;
  notificationTitlePrefix: string;
  emailSendingEnabled: string;
};

export type AppConfig = AdminConfig;

export const getAppConfigApi = async (): Promise<{
  config?: AppConfig;
  error?: string;
}> => {
  try {
    const res = await fetch(baseURL + 'v2/config/app', { method: 'GET' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return { config: data as AppConfig };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const getDataChangedApi = async (): Promise<{
  dataChangedAt?: string | null;
  error?: string;
}> => {
  try {
    const res = await fetch(baseURL + 'v2/config/data-changed', {
      method: 'GET',
    });
    const data = (await res.json().catch(() => ({}))) as {
      dataChangedAt?: string | null;
      error?: string;
    };
    if (!res.ok) return { error: data?.error || 'Failed' };
    return { dataChangedAt: data.dataChangedAt ?? null };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const getAdminConfigApi = async (
  authToken: string
): Promise<{ config?: AdminConfig; error?: string }> => {
  try {
    const q = new URLSearchParams({ authToken });
    const res = await fetch(baseURL + 'v2/admin/config?' + q.toString(), {
      method: 'GET',
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return { config: data as AdminConfig };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const putAdminConfigApi = async (
  authToken: string,
  config: Partial<AdminConfig>
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, ...config }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const getPricingMessageApi = async (): Promise<{
  value?: string;
  error?: string;
}> => {
  try {
    const res = await fetch(baseURL + 'v2/config/pricing-message', {
      method: 'GET',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return { value: (data as { value?: string }).value ?? '' };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const putPricingMessageApi = async (
  authToken: string,
  value: string
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/config/pricing-message', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, value }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const postAdminNotificationsApi = async (
  authToken: string,
  payload: {
    title: string;
    body: string;
    channels?: ('inapp' | 'email' | 'phone')[];
    sendToAll?: boolean;
    recipientIds?: number[];
    emails?: string[];
    phones?: string[];
  }
): Promise<{ error?: string; count?: number }> => {
  try {
    const res = await fetch(baseURL + 'v2/admin/notifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, ...payload }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return { count: (data as { count?: number }).count };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

type AdminSentNotification = {
  title: string;
  body: string;
  sentAt: string;
  recipientCount: number;
};

export const getAdminNotificationsSentApi = async (
  authToken: string
): Promise<{ sent?: AdminSentNotification[]; error?: string }> => {
  try {
    const res = await fetch(
      baseURL +
        'v2/admin/notifications/sent?authToken=' +
        encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { sent?: AdminSentNotification[]; error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: data?.error || 'Failed' };
    return { sent: data.sent ?? [] };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export type AdminIncomingItem = {
  id: number;
  type: string;
  userId: number;
  email: string | null;
  phone: string | null;
  displayName: string | null;
  requestedAt: string | null;
  handledAt: string | null;
  handledByUserId: number | null;
};

export const getAdminNotificationsIncomingApi = async (
  authToken: string
): Promise<{ incoming?: AdminIncomingItem[]; error?: string }> => {
  try {
    const res = await fetch(
      baseURL +
        'v2/admin/notifications/incoming?authToken=' +
        encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { incoming?: AdminIncomingItem[]; error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: data?.error || 'Failed' };
    return { incoming: data.incoming ?? [] };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const markAdminIncomingHandledApi = async (
  authToken: string,
  id: number
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(
      baseURL + 'v2/admin/notifications/incoming/' + id + '/handled',
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authToken }),
      }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    handleAuthFailure(res, data);
    if (!res.ok)
      return { error: (data as { error?: string })?.error || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

type MyNotification = {
  id: number;
  title: string;
  body: string;
  read: boolean;
  createdAt?: string;
};

export const getMyNotificationsApi = async (
  authToken: string
): Promise<{ notifications?: MyNotification[]; error?: string }> => {
  try {
    const res = await fetch(
      baseURL + 'v2/notifications?authToken=' + encodeURIComponent(authToken),
      { method: 'GET' }
    );
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { notifications?: MyNotification[]; error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: data?.error || 'Failed' };
    return { notifications: data.notifications ?? [] };
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const patchNotificationReadApi = async (
  authToken: string,
  notificationId: number
): Promise<{ error?: string }> => {
  try {
    const url =
      baseURL +
      'v2/notifications/' +
      notificationId +
      '/read?authToken=' +
      encodeURIComponent(authToken);
    const res = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: (data?.error as string) || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const patchAllNotificationsReadApi = async (
  authToken: string
): Promise<{ error?: string }> => {
  try {
    const url =
      baseURL +
      'v2/notifications/mark-all-read?authToken=' +
      encodeURIComponent(authToken);
    const res = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: (data?.error as string) || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const deleteNotificationApi = async (
  authToken: string,
  notificationId: number
): Promise<{ error?: string }> => {
  try {
    const url =
      baseURL +
      'v2/notifications/' +
      notificationId +
      '?authToken=' +
      encodeURIComponent(authToken);
    const res = await fetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: (data?.error as string) || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const deleteNotificationsBulkApi = async (
  authToken: string,
  ids: number[]
): Promise<{ error?: string }> => {
  try {
    const url =
      baseURL +
      'v2/notifications/bulk?authToken=' +
      encodeURIComponent(authToken);
    const res = await fetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken, ids }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: (data?.error as string) || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};

export const deleteAllNotificationsApi = async (
  authToken: string
): Promise<{ error?: string }> => {
  try {
    const url =
      baseURL +
      'v2/notifications/all?authToken=' +
      encodeURIComponent(authToken);
    const res = await fetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authToken }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<
      string,
      unknown
    > & { error?: string };
    handleAuthFailure(res, data);
    if (!res.ok) return { error: (data?.error as string) || 'Failed' };
    return {};
  } catch (e) {
    console.error(e);
    return { error: 'Request failed' };
  }
};
